import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { PORTFOLIO_ITEMS, hlsSrcFromVideoSrc } from '../data/portfolioData';
import { PORTFOLIO_DE } from '../data/portfolioDe';
import { useLang, localizeItem } from '../i18n/LanguageContext';
import { SITE_ORIGIN } from './Seo';
import styles from './CaseStudy.module.css';

const StreamPlayer = lazy(() => import('./StreamPlayer'));

function firstSentence(text) {
  if (!text) return '';
  const match = text.match(/^(.*?[.!?])(\s|$)/);
  return match ? match[1].trim() : text.trim();
}

function clampDescription(text, min = 140, max = 160) {
  const clean = (text || '').replace(/\s+/g, ' ').trim();
  if (clean.length <= max) return clean;
  const slice = clean.slice(0, max);
  const lastSpace = slice.lastIndexOf(' ');
  const trimmed = lastSpace > min ? slice.slice(0, lastSpace) : slice;
  return `${trimmed.replace(/[,;:.\s]+$/, '')}...`;
}

function buildDescription(item, lang) {
  if (item.campaign && item.campaign.intro) {
    const sentence = firstSentence(item.campaign.intro);
    if (sentence) return clampDescription(sentence);
  }
  const extras = [item.client, item.event, item.cityYear].filter(Boolean).join(' - ');
  const base =
    lang === 'de'
      ? `${item.title} — eine Fallstudie von EDELNAD Productions${extras ? ` (${extras})` : ''}`
      : `${item.title} - a case study by EDELNAD Productions${extras ? ` (${extras})` : ''}`;
  return clampDescription(base);
}

function pageTitle(item) {
  const title = `${item.title} | EDELNAD Productions`;
  return title.length > 60 ? `${item.title} | EDELNAD` : title;
}

function absoluteImage(posterSrc) {
  if (!posterSrc) return `${SITE_ORIGIN}/images/services/conference-coverage.png`;
  if (posterSrc.startsWith('http')) return posterSrc;
  return `${SITE_ORIGIN}${posterSrc}`;
}

// Meta field labels per language.
const META_LABELS = {
  en: {
    client: 'Client', event: 'Event', cityYear: 'City & Year', deliverable: 'Deliverable',
    crew: 'Crew', shootNotes: 'Shoot notes', outcome: 'Outcome',
    role: 'Role', years: 'Years', engineer: 'Engineer', city: 'City', year: 'Year',
  },
  de: {
    client: 'Kund:in', event: 'Event', cityYear: 'Stadt & Jahr', deliverable: 'Lieferung',
    crew: 'Team', shootNotes: 'Dreh-Notizen', outcome: 'Ergebnis',
    role: 'Rolle', years: 'Jahre', engineer: 'Engineer:in', city: 'Stadt', year: 'Jahr',
  },
};

const META_FIELDS = [
  { key: 'client', labelKey: 'client' },
  { key: 'event', labelKey: 'event' },
  { key: 'cityYear', labelKey: 'cityYear' },
  { key: 'deliverable', labelKey: 'deliverable' },
  { key: 'crew', labelKey: 'crew' },
  { key: 'shootNotes', labelKey: 'shootNotes' },
  { key: 'outcome', labelKey: 'outcome' },
];

const CAMPAIGN_HEADER_FIELDS = [
  { key: 'role', labelKey: 'role' },
  { key: 'years', labelKey: 'years' },
];

function useJsonLd(item, lang) {
  useEffect(() => {
    if (!item) return undefined;

    let videoSrc = item.videoSrc;
    let posterSrc = item.posterSrc;
    let titleForLd = item.title;
    let yearForLd = '';

    if (item.campaign) {
      const firstWithVideo = item.campaign.products
        .flatMap((p) => p.shoots)
        .find((s) => s.videoSrc);
      if (firstWithVideo) {
        videoSrc = firstWithVideo.videoSrc;
        posterSrc = firstWithVideo.posterSrc || posterSrc;
        titleForLd = firstWithVideo.artist
          ? `${item.title} — ${firstWithVideo.artist}`
          : item.title;
        yearForLd = firstWithVideo.year || '';
      } else {
        videoSrc = '';
        posterSrc = '';
      }
    }

    const hlsSrc = hlsSrcFromVideoSrc(videoSrc);
    const yearMatch = yearForLd.match(/(\d{4})/);
    const fallbackYearMatch = (item.cityYear || '').match(/(\d{4})/);
    const uploadYear = (yearMatch && yearMatch[1])
      || (fallbackYearMatch && fallbackYearMatch[1])
      || String(new Date().getFullYear());
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'VideoObject',
      name: titleForLd,
      uploadDate: `${uploadYear}-01-01`,
      thumbnailUrl: posterSrc || undefined,
      contentUrl: hlsSrc || undefined,
      embedUrl: videoSrc || undefined,
      inLanguage: lang,
    });
    document.head.appendChild(script);
    return () => {
      document.head.removeChild(script);
    };
  }, [item, lang]);
}

function MetaRows({ rows }) {
  return (
    <dl className={styles.meta}>
      {rows.map(({ label, value }) => (
        <div className={styles.metaRow} key={label}>
          <dt className={styles.metaLabel}>{label}</dt>
          <dd className={styles.metaValue}>{value}</dd>
        </div>
      ))}
    </dl>
  );
}

function ShootBlock({ shoot, labels, loadingLabel, comingSoonLabel }) {
  const metaRows = [
    shoot.engineer ? { label: labels.engineer, value: shoot.engineer } : null,
    shoot.city ? { label: labels.city, value: shoot.city } : null,
    shoot.year ? { label: labels.year, value: shoot.year } : null,
  ].filter(Boolean);

  return (
    <div className={styles.shoot}>
      <h3 className={styles.shootTitle}>{shoot.artist}</h3>
      {shoot.character && <p className={styles.character}>{shoot.character}</p>}
      {metaRows.length > 0 && <MetaRows rows={metaRows} />}
      <div className={styles.videoWrap}>
        {shoot.youtubeId ? (
          <div className={styles.youtubeWrap}>
            <iframe
              src={`https://www.youtube-nocookie.com/embed/${shoot.youtubeId}`}
              title={`${shoot.artist} film`}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              loading="lazy"
            />
          </div>
        ) : shoot.videoSrc ? (
          <Suspense fallback={<div className={styles.placeholder}>{loadingLabel}</div>}>
            <StreamPlayer videoSrc={shoot.videoSrc} />
          </Suspense>
        ) : (
          <div className={styles.comingSoon}>
            <p>{comingSoonLabel}</p>
          </div>
        )}
      </div>
    </div>
  );
}

const STREAM_THUMB_BASE = 'https://customer-8tlh0ntb488ziae5.cloudflarestream.com';

function shootThumbnail(shoot) {
  if (shoot.posterSrc) return shoot.posterSrc;
  if (shoot.youtubeId) {
    return `https://img.youtube.com/vi/${shoot.youtubeId}/hqdefault.jpg`;
  }
  if (shoot.videoSrc) {
    const match = shoot.videoSrc.match(/cloudflarestream\.com\/([a-f0-9]+)\/iframe/i);
    if (match) {
      return `${STREAM_THUMB_BASE}/${match[1]}/thumbnails/thumbnail.jpg`;
    }
  }
  return null;
}

function ChevronLeft() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path d="M12 5l-5 5 5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ChevronRight() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path d="M8 5l5 5-5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function CaseStudyModal({ shoot, onClose, closeLabel, loadingLabel }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  return createPortal(
    <div
      className={styles.modal}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={`${shoot.artist} film`}
    >
      <div className={styles.modalBackdrop} />
      <div className={styles.modalVideo} onClick={(e) => e.stopPropagation()}>
        <button type="button" className={styles.modalClose} onClick={onClose} aria-label={closeLabel}>
          ×
        </button>
        {shoot.youtubeId ? (
          <div className={styles.youtubeWrap}>
            <iframe
              src={`https://www.youtube-nocookie.com/embed/${shoot.youtubeId}?autoplay=1`}
              title={`${shoot.artist} film`}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            />
          </div>
        ) : (
          <Suspense fallback={<div className={styles.placeholder}>{loadingLabel}</div>}>
            <StreamPlayer videoSrc={shoot.videoSrc} />
          </Suspense>
        )}
      </div>
    </div>,
    document.body,
  );
}

function ProductRow({ product, onCardClick, labels }) {
  const trackRef = useRef(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  const updateArrows = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 8);
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 8);
  }, []);

  useEffect(() => {
    if (product.shoots.length === 0) return undefined;
    const el = trackRef.current;
    if (!el) return undefined;
    updateArrows();
    el.addEventListener('scroll', updateArrows, { passive: true });
    const ro = new ResizeObserver(updateArrows);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', updateArrows);
      ro.disconnect();
    };
  }, [updateArrows, product.shoots.length]);

  const scroll = (dir) => {
    const el = trackRef.current;
    if (!el) return;
    const amount = el.clientWidth * 0.75;
    el.scrollBy({ left: dir * amount, behavior: 'smooth' });
  };

  return (
    <section className={styles.product}>
      <h2 className={styles.rowLabel}>{product.name}</h2>
      {product.shoots.length === 0 ? (
        <p className={styles.rowEmpty}>{labels.filmsComingSoon}</p>
      ) : (
        <div className={styles.rowOuter}>
          {canLeft && (
            <button
              className={`${styles.scrollBtn} ${styles.scrollBtnLeft}`}
              onClick={() => scroll(-1)}
              aria-label={`Scroll ${product.name} left`}
            >
              <ChevronLeft />
            </button>
          )}
          <div className={styles.rowTrack} ref={trackRef}>
            {product.shoots.map((shoot, i) => {
              const thumb = shootThumbnail(shoot);
              return (
                <button
                  type="button"
                  className={styles.card}
                  key={`${shoot.artist}-${i}`}
                  onClick={() => onCardClick(shoot)}
                  aria-label={`Play ${shoot.artist} film`}
                >
                  <div className={styles.cardImgWrap}>
                    {thumb ? (
                      <img
                        src={thumb}
                        alt={shoot.artist}
                        className={styles.cardImg}
                        loading="lazy"
                        decoding="async"
                      />
                    ) : (
                      <div className={styles.cardImgPlaceholder} />
                    )}
                  </div>
                  <span className={styles.cardCaption}>{shoot.artist}</span>
                </button>
              );
            })}
          </div>
          {canRight && (
            <button
              className={`${styles.scrollBtn} ${styles.scrollBtnRight}`}
              onClick={() => scroll(1)}
              aria-label={`Scroll ${product.name} right`}
            >
              <ChevronRight />
            </button>
          )}
        </div>
      )}
    </section>
  );
}

function CampaignLayout({ item, nextSlug }) {
  const { t, buildPath, lang } = useLang();
  const labels = META_LABELS[lang] ?? META_LABELS.en;
  const c = item.campaign;
  const headerRows = CAMPAIGN_HEADER_FIELDS
    .map(({ key, labelKey }) => (c[key] ? { label: labels[labelKey], value: c[key] } : null))
    .filter(Boolean);

  const [activeShoot, setActiveShoot] = useState(null);

  return (
    <>
      <Link to={buildPath('/#work')} className={styles.back}>
        {t('caseStudy.back')}
      </Link>

      <p className="section-label">{t('caseStudy.campaign')}</p>
      <h1 className={styles.title}>{item.title}</h1>

      {item.posterSrc && (
        <div className={styles.posterBanner}>
          <img src={absoluteImage(item.posterSrc)} alt={`${item.title} campaign poster`} loading="eager" />
        </div>
      )}

      {headerRows.length > 0 && <MetaRows rows={headerRows} />}

      <p className={styles.lead}>{c.intro}</p>

      {c.layout === 'rows'
        ? c.products.map((product) => (
            <ProductRow
              key={product.name}
              product={product}
              onCardClick={setActiveShoot}
              labels={{ filmsComingSoon: t('caseStudy.filmsComingSoon') }}
            />
          ))
        : c.products.map((product) => (
            <section className={styles.product} key={product.name}>
              <h2 className={styles.productTitle}>{product.name}</h2>
              {product.shoots.map((shoot) => (
                <ShootBlock
                  key={shoot.artist}
                  shoot={shoot}
                  labels={labels}
                  loadingLabel={t('caseStudy.loadingPlayer')}
                  comingSoonLabel={t('caseStudy.videoComingSoon')}
                />
              ))}
            </section>
          ))}

      {c.outcome && (
        <p className={styles.outcome}>
          <span className={styles.outcomeLabel}>{t('caseStudy.outcomeLabel')}</span>
          {c.outcome}
        </p>
      )}

      {c.editCredits && <p className={styles.editCredits}>{c.editCredits}</p>}

      {nextSlug && (
        <Link to={buildPath(`/work/${nextSlug}`)} className={styles.next}>
          {t('caseStudy.next')}
        </Link>
      )}

      {activeShoot && (
        <CaseStudyModal
          shoot={activeShoot}
          onClose={() => setActiveShoot(null)}
          closeLabel={t('video.close')}
          loadingLabel={t('caseStudy.loadingPlayer')}
        />
      )}
    </>
  );
}

function SingleProjectLayout({ item, nextSlug }) {
  const { t, buildPath, lang } = useLang();
  const labels = META_LABELS[lang] ?? META_LABELS.en;
  const metaRows = META_FIELDS
    .map(({ key, labelKey }) => (item[key] ? { label: labels[labelKey], value: item[key] } : null))
    .filter(Boolean);

  return (
    <>
      <Link to={buildPath('/#work')} className={styles.back}>
        {t('caseStudy.back')}
      </Link>

      <p className="section-label">{t('caseStudy.caseStudy')}</p>
      <h1 className={styles.title}>{item.title}</h1>

      {item.posterSrc && (
        <div className={styles.posterBanner}>
          <img src={absoluteImage(item.posterSrc)} alt={`${item.title} poster`} loading="eager" />
        </div>
      )}

      <div className={styles.videoWrap}>
        {item.videoSrc ? (
          <Suspense fallback={<div className={styles.placeholder}>{t('caseStudy.loadingPlayer')}</div>}>
            <StreamPlayer videoSrc={item.videoSrc} />
          </Suspense>
        ) : (
          <div className={styles.placeholder}>
            <p>{t('caseStudy.videoComingSoon')}</p>
          </div>
        )}
      </div>

      {metaRows.length > 0 && <MetaRows rows={metaRows} />}

      {item.body && (
        <div className={styles.body}>
          {item.body.split('\n\n').map((para, i) => (
            <p key={i}>{para}</p>
          ))}
        </div>
      )}

      {nextSlug && (
        <Link to={buildPath(`/work/${nextSlug}`)} className={styles.next}>
          {t('caseStudy.next')}
        </Link>
      )}
    </>
  );
}

export default function CaseStudy() {
  const { slug } = useParams();
  const { lang, t, buildPath } = useLang();
  const enItem = useMemo(
    () => PORTFOLIO_ITEMS.find((i) => i.slug === slug),
    [slug],
  );
  const item = useMemo(
    () => localizeItem(enItem, lang, PORTFOLIO_DE),
    [enItem, lang],
  );

  // Scroll to top on mount and whenever the slug changes.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  useJsonLd(item, lang);

  // Next project slug (wraps around the portfolio list)
  const nextSlug = (() => {
    if (!enItem) return '';
    const idx = PORTFOLIO_ITEMS.findIndex((i) => i.slug === enItem.slug);
    if (idx === -1) return '';
    const next = PORTFOLIO_ITEMS[(idx + 1) % PORTFOLIO_ITEMS.length];
    return next ? next.slug : '';
  })();

  if (!enItem) {
    return (
      <section className={styles.section}>
        <div className={`container ${styles.notFound}`}>
          <p className="section-label">404</p>
          <h1 className={styles.title}>{t('caseStudy.notFound')}</h1>
          <p className={styles.body}>{t('caseStudy.notFoundBody')}</p>
          <Link to={buildPath('/')} className={styles.back}>
            {t('caseStudy.backHome')}
          </Link>
        </div>
      </section>
    );
  }

  const title = pageTitle(item);
  const description = buildDescription(item, lang);
  const langPath = lang === 'de' ? `/de/work/${enItem.slug}` : `/work/${enItem.slug}`;
  const enPath = `/work/${enItem.slug}`;
  const dePath = `/de/work/${enItem.slug}`;
  const canonical = `${SITE_ORIGIN}${langPath}`;
  const ogImage = absoluteImage(item.posterSrc);

  return (
    <section className={styles.section}>
      <Helmet>
        <html lang={lang} />
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonical} />
        <link rel="alternate" hrefLang="en" href={`${SITE_ORIGIN}${enPath}`} />
        <link rel="alternate" hrefLang="de" href={`${SITE_ORIGIN}${dePath}`} />
        <link rel="alternate" hrefLang="x-default" href={`${SITE_ORIGIN}${enPath}`} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="EDELNAD Productions" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonical} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:locale" content={lang === 'de' ? 'de_DE' : 'en_US'} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={ogImage} />
      </Helmet>
      <div className={`container ${styles.inner}`}>
        {item.campaign ? (
          <CampaignLayout item={item} nextSlug={nextSlug} />
        ) : (
          <SingleProjectLayout item={item} nextSlug={nextSlug} />
        )}
      </div>
    </section>
  );
}
